// 前端状态机镜像 + 用户侧 user_done / user_cancel 触发。
//
// 与 agent/internal/state/machine.go 的 State 常量对齐。只是"镜像",不参与转移判定。
// agent 侧通过 DataChannel 把 state.snapshot 和 state.transition 推过来,前端据此
// 渲染进度 + 启禁按钮。
//
// 用户按「完成」:发 `state.user_done` → agent 翻成 EvUserDone。
// 用户按「取消」:发 `state.user_cancel` → agent 翻成 EvForceCancel(reason=user_cancel)。

import type { Send, WireEnvelope } from '@no-vnc-web/input';
import { makeEnvelope } from '@no-vnc-web/input';

// ----- 状态名(对齐 Go state.State) -----

export const HandoffStates = [
  'AGENT_RUNNING',
  'HANDOFF_REQUESTED',
  'AWAITING_USER',
  'USER_CONTROLLING',
  'USER_COMPLETED',
  'VALIDATING',
  'AGENT_RESUMED',
  'HANDOFF_FAILED',
] as const;

export type HandoffState = (typeof HandoffStates)[number];

export function isTerminal(s: HandoffState): boolean {
  return s === 'AGENT_RESUMED' || s === 'HANDOFF_FAILED';
}

// ----- Wire 事件(与 agent 对接;type 命名在 input 协议族之外,用 "state." 前缀) -----

export const WireTypeStateSnapshot = 'state.snapshot';
export const WireTypeStateTransition = 'state.transition';
export const WireTypeStateUserDone = 'state.user_done';
export const WireTypeStateUserCancel = 'state.user_cancel';

export interface StateSnapshot {
  state: HandoffState;
  session_id?: string;
  task_id?: string;
  reason?: string; // present when state=HANDOFF_FAILED
}

export interface StateTransition {
  from: HandoffState;
  to: HandoffState;
}

export interface UserDonePayload {
  session_id?: string;
}

export interface UserCancelPayload {
  session_id?: string;
  reason?: string;
}

// ----- HandoffMirror:保存 agent 推过来的状态并通知订阅者 -----

export interface HandoffView {
  current: HandoffState;
  sessionId?: string;
  taskId?: string;
  failureReason?: string;
  canComplete: boolean;
  canCancel: boolean;
}

type Listener = (view: HandoffView) => void;

export class HandoffMirror {
  private current: HandoffState = 'AGENT_RUNNING';
  private sessionId?: string;
  private taskId?: string;
  private failureReason?: string;
  private listeners: Set<Listener> = new Set();

  /** 当前视图(只读快照)。*/
  view(): HandoffView {
    return {
      current: this.current,
      sessionId: this.sessionId,
      taskId: this.taskId,
      failureReason: this.failureReason,
      canComplete: this.current === 'USER_CONTROLLING',
      // 从会话建立到进入终态前都允许取消(含 USER_CONTROLLING / USER_COMPLETED / VALIDATING)。
      canCancel: !isTerminal(this.current) && this.current !== 'AGENT_RUNNING',
    };
  }

  /** 订阅状态变化。返回取消订阅函数。*/
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.view());
    return () => this.listeners.delete(fn);
  }

  /**
   * 处理一条可能是状态事件的 envelope。
   * 返回 true 表示命中状态类;false 表示调用方应交给其他路由(如 input)。
   */
  handleEnvelope(env: WireEnvelope): boolean {
    switch (env.type) {
      case WireTypeStateSnapshot: {
        const p = env.payload as StateSnapshot;
        if (!isHandoffState(p.state)) return true; // 已命中类别但 payload 不合法
        this.current = p.state;
        if (p.session_id !== undefined) this.sessionId = p.session_id;
        if (p.task_id !== undefined) this.taskId = p.task_id;
        this.failureReason = p.reason;
        this.emit();
        return true;
      }
      case WireTypeStateTransition: {
        const p = env.payload as StateTransition;
        if (!isHandoffState(p.to)) return true;
        this.current = p.to;
        // 进入终态时清理中间态的 failureReason(若 payload 未带)
        if (isTerminal(this.current) && !this.failureReason && this.current === 'HANDOFF_FAILED') {
          this.failureReason = `reached ${this.current}`;
        }
        this.emit();
        return true;
      }
      default:
        return false;
    }
  }

  private emit(): void {
    const v = this.view();
    for (const fn of this.listeners) fn(v);
  }
}

function isHandoffState(s: unknown): s is HandoffState {
  return typeof s === 'string' && (HandoffStates as readonly string[]).includes(s);
}

// ----- 用户动作发送器 -----

export function sendUserDone(send: Send, sessionId?: string): void {
  send(makeEnvelope<UserDonePayload>(WireTypeStateUserDone, { session_id: sessionId }));
}

export function sendUserCancel(send: Send, sessionId?: string, reason?: string): void {
  send(
    makeEnvelope<UserCancelPayload>(WireTypeStateUserCancel, {
      session_id: sessionId,
      reason: reason ?? 'user_cancel',
    }),
  );
}
